import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { cacheableData } from "../../../excel/workbook/cache";
import { useReportOptionFeatureFlags } from "../../hooks/useFeatureFlag";
import type { ReportOptionDefinition, ReportOptionDefinitionDropdown } from "../../../excel/reports/api";
import { dropdownSlotProps } from "../../styles/dropdown";
import { ediphiPalette } from "../../styles/colors";

/** Definitions the current user can actually see, with flag-gated ones removed. */
export const useVisibleReportOptions = (
  optionDefinitions?: ReportOptionDefinition[] | null
): ReportOptionDefinition[] => {
  const flagValues = useReportOptionFeatureFlags();

  return useMemo(() => {
    if (!optionDefinitions?.length) return [];
    return optionDefinitions.filter((def) => !def.featureFlag || flagValues[def.featureFlag]);
  }, [optionDefinitions, flagValues]);
};

interface ReportOptionControlsProps {
  definitions: ReportOptionDefinition[];
  options: cacheableData;
  onOptionChange: (key: string) => (value: boolean | string | string[]) => Promise<void>;
  /** Run after a dropdown commits. Checkbox changes deliberately do not trigger this. */
  onDropdownCommit: () => Promise<void>;
  /** Dropdown choices are fetched only while the controls are on screen. */
  active: boolean;
  loading?: boolean;
  /** Named ranges the sheet has, out of those the definitions require; null until known. */
  presentNamedRanges?: Set<string> | null;
}

const emptyOptionFor = (def: ReportOptionDefinitionDropdown) =>
  ({ [def.valueKey]: "", [def.labelKey]: def.emptyOptionLabel }) as Record<string, unknown>;

export const ReportOptionControls: React.FC<ReportOptionControlsProps> = ({
  definitions,
  options,
  onOptionChange,
  onDropdownCommit,
  active,
  loading = false,
  presentNamedRanges = null,
}) => {
  // Hidden until the sheet has been checked, so the control never flashes on where it does nothing
  const missingNamedRange = (def: ReportOptionDefinition) =>
    !!def.requiredNamedRanges && !def.requiredNamedRanges.some((name) => presentNamedRanges?.has(name));

  /** Options for each dropdown, keyed by option definition key */
  const [dropdownOptionsByKey, setDropdownOptionsByKey] = useState<Record<string, Record<string, unknown>[]>>({});

  const hasCheckboxes = definitions.some((d) => d.controlType === "checkbox");
  const hasDropdowns = definitions.some((d) => d.controlType === "dropdown");
  const lastDropdownKey = useMemo(() => {
    const dropdownDefs = definitions.filter((d) => d.controlType === "dropdown");
    return dropdownDefs.length > 0 ? dropdownDefs[dropdownDefs.length - 1].key : null;
  }, [definitions]);

  useEffect(() => {
    if (!hasDropdowns || !active) return;
    const dropdownDefs = definitions.filter((d): d is ReportOptionDefinitionDropdown => d.controlType === "dropdown");
    const fetchAll = async () => {
      const next: Record<string, Record<string, unknown>[]> = {};
      await Promise.all(
        dropdownDefs.map(async (def) => {
          try {
            const data = await def.getDropdownOptions();
            next[def.key] = data ?? [];
          } catch (err) {
            console.warn(`Failed to fetch dropdown options for ${def.key}:`, err);
            next[def.key] = [];
          }
        })
      );
      setDropdownOptionsByKey((prev) => ({ ...prev, ...next }));
    };
    fetchAll();
  }, [hasDropdowns, active, definitions]);

  return (
    <>
      {definitions.map((def) => {
        if (def.controlType === "checkbox") {
          const isDisabled = loading || (def.disabledWhen?.(options) ?? false);
          const value = options[def.key];
          const checked = typeof value === "boolean" ? value : false;
          if (missingNamedRange(def)) return;
          return (
            <FormControlLabel
              key={def.key}
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => onOptionChange(def.key)(e.target.checked)}
                  disabled={isDisabled}
                  size="small"
                />
              }
              label={def.label}
            />
          );
        }
        if (def.controlType === "dropdown") {
          const choices = dropdownOptionsByKey[def.key] ?? [];
          const isDisabled = loading || (def.disabledWhen?.(options) ?? false);
          const isMultiple = def.multiple === true;
          const emptyOpt = emptyOptionFor(def);
          const currentStored = options[def.key];
          const selectedIds = Array.isArray(currentStored)
            ? new Set(currentStored.map((value) => String(value).trim()).filter((value) => value.length > 0))
            : new Set<string>();
          const multiValue = choices.filter((choice) => selectedIds.has(String(choice[def.valueKey] ?? "").trim()));
          const singleOptions = [emptyOpt, ...choices];
          const singleValue = choices.find((o) => String(o[def.valueKey]) === String(currentStored ?? "")) ?? emptyOpt;
          if (missingNamedRange(def)) return;
          return (
            <Box
              key={def.key}
              sx={{
                marginBottom: def.key === lastDropdownKey ? 2 : 0,
                paddingTop: hasCheckboxes ? 2 : 0,
              }}
            >
              {isMultiple ? (
                <Autocomplete<Record<string, unknown>, true, false, false>
                  multiple
                  options={choices}
                  value={multiValue}
                  onChange={async (_event, newValue) => {
                    try {
                      const selectedValues = newValue
                        .map((option) => String(option?.[def.valueKey] ?? "").trim())
                        .filter((value) => value.length > 0);
                      await onOptionChange(def.key)(selectedValues);
                      await onDropdownCommit();
                    } catch {
                      // Error already shown via banner
                    }
                  }}
                  isOptionEqualToValue={(a, b) => String(a?.[def.valueKey] ?? "") === String(b?.[def.valueKey] ?? "")}
                  getOptionLabel={(option) => String(option?.[def.labelKey] ?? def.emptyOptionLabel)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={def.label}
                      placeholder={multiValue.length === 0 ? def.emptyOptionLabel : ""}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      variant="outlined"
                      sx={{
                        "& .MuiInputBase-input::placeholder": {
                          color: ediphiPalette.textBlack,
                          opacity: 1,
                        },
                      }}
                    />
                  )}
                  disabled={isDisabled}
                  slotProps={dropdownSlotProps}
                />
              ) : (
                <Autocomplete<Record<string, unknown>, false, false, false>
                  multiple={false}
                  options={singleOptions}
                  value={singleValue}
                  onChange={async (_event, newValue) => {
                    try {
                      const raw = newValue?.[def.valueKey];
                      const value = typeof raw === "string" ? raw : String(raw ?? "");
                      await onOptionChange(def.key)(value);
                      await onDropdownCommit();
                    } catch {
                      // Error already shown via banner
                    }
                  }}
                  isOptionEqualToValue={(a, b) => String(a?.[def.valueKey] ?? "") === String(b?.[def.valueKey] ?? "")}
                  getOptionLabel={(option) => String(option?.[def.labelKey] ?? def.emptyOptionLabel)}
                  renderInput={(params) => <TextField {...params} label={def.label} size="small" variant="outlined" />}
                  disabled={isDisabled}
                  slotProps={dropdownSlotProps}
                />
              )}
            </Box>
          );
        }
        return null;
      })}
    </>
  );
};

export default ReportOptionControls;
